import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Plus, ClipboardText, PaperPlaneTilt, CheckCircle, ChartBar } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { toast } from "@/lib/toast";
import { Button } from "@/components/ui/button";
import { TestCard } from "@/components/admin/tests/TestCard";
import { TestSkeletonCard } from "@/components/admin/tests/TestSkeletonCard";
import { TestEmptyState } from "@/components/admin/tests/TestEmptyState";
import { TestDeleteDialog } from "@/components/admin/tests/TestDeleteDialog";
import { throwIfNotOk } from "@/lib/api-error";
import type { Test } from "@/types/test";

function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Plus;
  label: string;
  value: number | string;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-border/50 bg-card p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        <div className="flex size-8 items-center justify-center rounded-lg bg-primary/10">
          <Icon className="size-4 text-primary" />
        </div>
      </div>
      <p
        className="mt-2 text-2xl font-bold text-foreground"
        style={{ fontFamily: "var(--font-heading)" }}
      >
        {value}
      </p>
    </div>
  );
}

export function TestsCatalogSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "tests"],
    queryFn: async () => {
      const res = await fetch("/api/tests");
      await throwIfNotOk(res, t("tests.admin.fetchError"));
      return res.json() as Promise<{ tests: Test[] }>;
    },
  });

  const { data: stats } = useQuery({
    queryKey: ["admin", "tests", "stats"],
    queryFn: async () => {
      const res = await fetch("/api/tests/stats");
      await throwIfNotOk(res, t("tests.admin.statsError"));
      return res.json() as Promise<{
        totals: { totalTests: number; totalSent: number; totalSubmitted: number; submissionRate: number | null };
      }>;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/tests/${id}`, { method: "DELETE" });
      if (res.status === 409) {
        const patch = await fetch(`/api/tests/${id}`, {
          method: "PATCH",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ isActive: false }),
        });
        await throwIfNotOk(patch, t("tests.admin.deactivateError"));
        return;
      }
      await throwIfNotOk(res, t("tests.admin.deleteError"));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "tests"] });
      setDeletingId(null);
      toast.success(t("tests.admin.deleteSuccess"));
    },
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-end">
        <Button asChild>
          <Link to="/dashboard/admin/tests/new">
            <Plus className="size-4" />
            {t("tests.admin.create")}
          </Link>
        </Button>
      </div>

      {stats && (
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard icon={ClipboardText} label={t("tests.admin.stats.totalTests")} value={stats.totals.totalTests} />
          <StatCard icon={PaperPlaneTilt} label={t("tests.admin.stats.totalSent")} value={stats.totals.totalSent} />
          <StatCard icon={CheckCircle} label={t("tests.admin.stats.totalSubmitted")} value={stats.totals.totalSubmitted} />
          <StatCard
            icon={ChartBar}
            label={t("tests.admin.stats.submissionRate")}
            value={stats.totals.submissionRate === null ? "—" : `${Math.round(stats.totals.submissionRate * 100)}%`}
          />
        </section>
      )}

      <section className="flex flex-col gap-3">
        {isLoading && (
          <>
            <TestSkeletonCard />
            <TestSkeletonCard />
            <TestSkeletonCard />
          </>
        )}

        {!isLoading && data && data.tests.length === 0 && <TestEmptyState />}

        {!isLoading && data && data.tests.length > 0 && (
          <ul className="flex flex-col gap-2">
            {data.tests.map((test, idx) => (
              <li key={test.id} className="animate-ink-rise" style={{ animationDelay: `${idx * 30}ms` }}>
                <TestCard
                  test={test}
                  index={idx + 1}
                  onEdit={(id) => navigate({ to: "/dashboard/admin/tests/$testId", params: { testId: id } })}
                  onDelete={setDeletingId}
                />
              </li>
            ))}
          </ul>
        )}
      </section>

      <TestDeleteDialog
        testId={deletingId}
        onClose={() => setDeletingId(null)}
        onConfirm={(id) => deleteMutation.mutate(id)}
      />
    </div>
  );
}
